import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BadgeCheck, AlertCircle } from "lucide-react";

interface WidgetStatus {
  installation_key: string;
  domain: string;
  last_ping: string | null;
  widget_version?: string;
}

const PING_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

const WidgetStatusTable: React.FC = () => {
  const [statuses, setStatuses] = useState<WidgetStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const [installationKeyMap, setInstallationKeyMap] = useState<Record<string, number>>({});

  const fetchStatuses = useCallback(async () => {
    setLoading(true);

    const { data: installations, error: iError } = await supabase
      .from("website_installations")
      .select("installation_key, domain");

    if (iError || !installations) {
      setLoading(false);
      setStatuses([]);
      setInstallationKeyMap({});
      return;
    }

    const keys = installations.map((i: any) => i.installation_key);
    if (keys.length === 0) {
      setStatuses([]);
      setInstallationKeyMap({});
      setLoading(false);
      return;
    }

    const { data: heartbeats, error: hError } = await supabase
      .from("widget_heartbeats")
      .select("installation_key, pinged_at, widget_version")
      .in("installation_key", keys)
      .order("pinged_at", { ascending: false });

    const heartbeatMap: Record<string, any> = {};
    if (heartbeats) {
      for (const hb of heartbeats) {
        if (!heartbeatMap[hb.installation_key]) {
          heartbeatMap[hb.installation_key] = hb;
        }
      }
    }

    const result: WidgetStatus[] = installations.map((i: any) => ({
      installation_key: i.installation_key,
      domain: i.domain,
      last_ping: heartbeatMap[i.installation_key]?.pinged_at || null,
      widget_version: heartbeatMap[i.installation_key]?.widget_version,
    }));

    const keyMap: Record<string, number> = {};
    result.forEach((r, idx) => {
      keyMap[r.installation_key] = idx;
    });

    setStatuses(result);
    setInstallationKeyMap(keyMap);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatuses();

    const channel = supabase
      .channel('public:widget_heartbeats')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'widget_heartbeats',
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const hb = payload.new;
            setStatuses((current) => {
              const idx = installationKeyMap[hb.installation_key];
              if (idx === undefined) return current;
              if (
                !current[idx].last_ping ||
                new Date(hb.pinged_at).getTime() > new Date(current[idx].last_ping!).getTime()
              ) {
                const updated = [...current];
                updated[idx] = {
                  ...updated[idx],
                  last_ping: hb.pinged_at,
                  widget_version: hb.widget_version,
                };
                return updated;
              }
              return current;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchStatuses, installationKeyMap]);

  const isOnline = (last_ping: string | null) => {
    if (!last_ping) return false;
    const pingTime = new Date(last_ping).getTime();
    const now = Date.now();
    return now - pingTime < PING_TIMEOUT_MS;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl font-bold">Live Widget Status</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center">Loading widget status...</div>
        ) : statuses.length === 0 ? (
          <div className="py-10 text-center">No widget installations found.</div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Installation Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Ping</TableHead>
                  <TableHead>Version</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statuses.map((s) => (
                  <TableRow key={s.installation_key}>
                    <TableCell className="max-w-xs truncate">{s.domain}</TableCell>
                    <TableCell className="font-mono text-xs">{s.installation_key}</TableCell>
                    <TableCell>
                      {isOnline(s.last_ping) ? (
                        <span className="flex items-center gap-1 text-green-700">
                          <BadgeCheck className="h-4 w-4 text-green-600" /> Online
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-700">
                          <AlertCircle className="h-4 w-4 text-red-600" /> Offline
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.last_ping
                        ? new Date(s.last_ping).toLocaleString()
                        : <span className="italic text-muted-foreground">Never</span>}
                    </TableCell>
                    <TableCell>
                      {s.widget_version || <span className="italic text-muted-foreground">N/A</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WidgetStatusTable;
